import { QRCodeSVG } from 'qrcode.react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Check, Printer, ChefHat, Share2, Trash2 } from 'lucide-react';
import { isLikelyIOS } from '@/lib/printBridge';

/**
 * Post-checkout "Order complete" dialog. Shows order number, totals, a QR
 * to the customer-facing tracking page, and primary actions: print receipt
 * and send to kitchen.
 *
 * iter666 — On iPad, a third button "iPrint" appears that hands the
 * receipt PDF to the iOS Share Sheet, letting the cashier pick Epson
 * iPrint as the destination (native iOS app with raw TCP-9100 access
 * to the printer — bypasses AirPrint discovery entirely).
 *
 * iter834 — Optional "Void this order" text button (manager-PIN gated
 * via VoidLastOrderDialog). Only renders when `onVoidOrder` is passed.
 */
export default function OrderCompleteDialog({
  open,
  onOpenChange,
  lastOrder,
  currencySymbol = 'SAR',
  onPrintReceipt,
  onSendKitchen,
  onSendIPrint,
  onVoidOrder,
}) {
  const showIPrint = isLikelyIOS() && typeof navigator !== 'undefined' && !!navigator.share;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Check size={24} className="text-success" />
            Order Complete!
          </DialogTitle>
        </DialogHeader>
        {lastOrder && (
          <div className="space-y-4">
            <div className="text-center py-4 bg-muted/50 rounded-xl">
              <p className="text-4xl font-bold font-outfit text-orange-600" data-testid="order-number">
                #{lastOrder.order_number}
              </p>
              <p className="text-muted-foreground">Order Number</p>
            </div>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span>Total</span>
                <span className="font-bold">{currencySymbol} {lastOrder.total?.toFixed(2)}</span>
              </div>
              <div className="flex justify-between">
                <span>Payment</span>
                <span className="capitalize">{lastOrder.payment_method}</span>
              </div>
              <div className="flex justify-between">
                <span>Type</span>
                <span className="capitalize">{lastOrder.order_type?.replace('_', ' ')}</span>
              </div>
            </div>
            {/* QR Code for Order Tracking */}
            <div className="text-center py-3 border-t border-dashed dark:border-stone-700">
              <QRCodeSVG
                value={`${window.location.origin}/track-order?id=${lastOrder.id || lastOrder.order_number}`}
                size={100}
                className="mx-auto"
                data-testid="receipt-qr-code"
              />
              <p className="text-[10px] text-muted-foreground mt-2">Scan to track your order</p>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" className="flex-1" onClick={onPrintReceipt} data-testid="print-receipt-btn">
                <Printer size={16} className="mr-2" />Receipt
              </Button>
              <Button
                className="flex-1 bg-warning hover:bg-warning"
                onClick={onSendKitchen}
                data-testid="send-kitchen"
              >
                <ChefHat size={16} className="mr-2" />Kitchen
              </Button>
            </div>
            {showIPrint && onSendIPrint && (
              <Button
                variant="outline"
                className="w-full border-sky-300 text-sky-700 hover:bg-sky-50"
                onClick={onSendIPrint}
                data-testid="send-iprint-btn"
                title="Hand the receipt PDF to Epson iPrint via iOS Share Sheet — works without AirPrint discovery"
              >
                <Share2 size={16} className="mr-2" />Send to Epson iPrint
              </Button>
            )}
            {onVoidOrder && (
              <button
                type="button"
                onClick={onVoidOrder}
                className="w-full text-xs text-error hover:text-error/80 hover:underline flex items-center justify-center gap-1 py-1"
                data-testid="void-order-btn"
                title="Requires manager PIN — reverses stock, ZATCA row retained"
              >
                <Trash2 size={12} /> Void this order (manager PIN)
              </button>
            )}
          </div>
        )}
        <DialogFooter>
          <Button onClick={() => onOpenChange(false)} className="w-full bg-orange-500 hover:bg-orange-600">
            New Order
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
